import clsx from "clsx";

import type { MeterTone } from "./meter";
import styles from "./segmented-meter.module.css";

export interface MeterSegment {
  /** How much of the whole this part is. */
  value: number;
  /** What the part means, which is what picks its colour. */
  tone: MeterTone;
  /** The part's legend entry, pre-formatted, e.g. `"Scanned 41,494"`. */
  label: string;
  /**
   * Listed in the legend but not drawn: its share of the bar is the empty track.
   * For a part that is just "the rest" (remaining stops), where painting it
   * would make the bar look full.
   */
  legendOnly?: boolean;
}

export interface SegmentedMeterProps {
  segments: ReadonlyArray<MeterSegment>;
  /**
   * The whole the segments are parts of. Defaults to their sum, so the bar is
   * full; pass a larger one and what is left over shows as empty track (stops
   * completed, out of all stops).
   */
  total?: number;
  /** The bar's accessible name, e.g. `"Scan quality: 95.28%"`. */
  "aria-label": string;
  "data-test-role"?: string;
}

const TONE_CLASS: Record<MeterTone, string> = {
  good: styles.toneGood!,
  fair: styles.toneFair!,
  poor: styles.tonePoor!,
  progress: styles.toneProgress!,
  idle: styles.toneIdle!,
};

/**
 * One bar in several colours, with a legend that names each part.
 *
 * Where `Meter` is one quantity out of a known range, this is a whole divided
 * into parts that each mean something different -- scanned against not scanned,
 * completed against active against not started. So it takes its parts as
 * `{ value, tone, label }` and draws the legend itself: the dots are the same
 * colours as the segments by construction, which is the thing a hand-built
 * legend beside a bar gets wrong.
 *
 * The bar is `role="img"` rather than a meter: a meter is a single value in a
 * range, and this has no one value to report. The legend below is real text, so
 * the parts and their counts are readable without seeing the colours.
 */
function SegmentedMeter({
  segments,
  total,
  "aria-label": ariaLabel,
  "data-test-role": testRole,
}: SegmentedMeterProps) {
  const whole = total ?? segments.reduce((sum, s) => sum + s.value, 0);
  const width = (value: number) =>
    whole > 0 ? `${Math.min(Math.max((value / whole) * 100, 0), 100)}%` : "0%";

  return (
    <div className={styles.root} data-test-role={testRole}>
      <div role="img" aria-label={ariaLabel} className={styles.bar}>
        {segments
          .filter((segment) => !segment.legendOnly)
          .map((segment) => (
            <span
              key={segment.label}
              className={clsx(styles.segment, TONE_CLASS[segment.tone])}
              style={{ width: width(segment.value) }}
            />
          ))}
      </div>
      <ul className={styles.legend}>
        {segments.map((segment) => (
          <li key={segment.label} className={styles.legendItem}>
            <span
              aria-hidden="true"
              className={clsx(styles.dot, TONE_CLASS[segment.tone])}
            />
            {segment.label}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default SegmentedMeter;
